import store, { readSavedStore, lastSeenRefreshToken, onBeforeDeferredWrite, saveStore, startWriting, stopWriting } from "@/core/store/PersistentStorage";
import eventBus from "@/core/services/eventBus";
import arcReadsApiClient from "@/app/services/arcReadsApiClient";
import rosterApiClient from "@/app/services/rosterApiClient";
import logService from "@/app/services/LogService";
import unleash from "@/core/services/unleashClient";
import { ref } from 'vue'

export class TransientRefreshError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'TransientRefreshError'
  }
}

const VALIDATION_CACHE_WINDOW = 5000 // 5 seconds
const EXPIRY_BUFFER_SECONDS = 30
const RETRY_MAX_ATTEMPTS = 3
const RETRY_BASE_DELAY_MS = 1000
const FAILURE_COOLDOWN_MS = 3000

// Another tab's refreshed session can be saved just after this tab's refresh is refused -- both
// sent the same token, and only one could have it -- so look again a couple of times before
// concluding the session is over.
const ADOPT_RETRY_ATTEMPTS = 2
const ADOPT_RETRY_DELAY_MS = 150

let lastValidationTimestamp = 0
let lastRefreshFailureTimestamp = 0
let refreshPromise: Promise<string | false> | null = null

const checkingForFeatures = ref(false)

const authenticationService = {
  currentTimeInSeconds() {
    return Math.floor(Date.now() / 1000);
  },
  getSettings() {
    if (this.isLoggedIn() == false) return {};
    const settings = JSON.parse(
      store.authentication.user.profile.app_settings || "{}"
    );
    return settings || {};
  },
  getSetting(prop) {
    const settings = this.getSettings();
    return settings == null ? null : settings[prop];
  },
  changeSetting(prop, value) {
    if (this.isLoggedIn() == false) return;
    const settings = this.getSettings();
    settings[prop] = value;
    store.authentication.user.profile.app_settings = JSON.stringify(settings);
    this.saveProfile();
  },
  saveProfile() {
    if (this.isLoggedIn() == false) return;
    arcReadsApiClient.put("/profile", store.authentication.user.profile);
  },
  isLoggedIn() {
    return this.currentUser() != null;
  },
  logout() {
    this.clearStore();
    window.location.href =
      import.meta.env.VITE_ARCID_HOST +
      "/logout?client_id=" +
      import.meta.env.VITE_ARCID_CLIENT_ID +
      "&post_logout_redirect_uri=" +
      window.location.origin;
  },
  currentUser() {
    return store.authentication.user || null;
  },

  async validateSession(): Promise<string | false> {
    if (refreshPromise) return refreshPromise;

    const now = Date.now();
    if (
      lastValidationTimestamp > 0 &&
      now - lastValidationTimestamp < VALIDATION_CACHE_WINDOW &&
      store.authentication.accessToken
    ) {
      return store.authentication.accessToken;
    }

    if (
      lastRefreshFailureTimestamp > 0 &&
      now - lastRefreshFailureTimestamp < FAILURE_COOLDOWN_MS
    ) {
      return false;
    }

    if (!store.authentication.refreshToken || store.authentication.expires_at == null) {
      return false;
    }

    if (store.authentication.expires_at > this.currentTimeInSeconds() + EXPIRY_BUFFER_SECONDS) {
      lastValidationTimestamp = Date.now();
      return store.authentication.accessToken;
    }

    refreshPromise = this.getNewAccessTokenWithRetry()
      .then(() => {
        lastValidationTimestamp = Date.now();
        return store.authentication.accessToken || false;
      })
      .catch((err) => {
        lastRefreshFailureTimestamp = Date.now();
        throw err;
      })
      .finally(() => {
        refreshPromise = null;
      }) as Promise<string | false>;

    return refreshPromise;
  },

  /**
   * Resolves once any token refresh under way has finished, whether or not it succeeded.
   *
   * ARC ID refresh tokens are single-use: it redeems the old one before answering. Leaving
   * the page mid-refresh lets that happen without the new one ever reaching the store, so
   * the next page holds only a spent token and is logged out on its first refresh.
   */
  whenRefreshSettled(): Promise<void> {
    if (!refreshPromise) return Promise.resolve();
    return refreshPromise.then(() => undefined, () => undefined);
  },

  loginSSO(authorizationCode) {
    const redirectUri = window.location.origin + "/oauth/arcid";
    startWriting();
    this.clearStore();
    return arcReadsApiClient
      .post("login", {
        code: authorizationCode,
        redirect_uri: redirectUri,
        client_id: import.meta.env.VITE_ARCID_CLIENT_ID,
      })
      .then((response) => {
        if (response && response.data) {
          this.processLoginResponse(response, true);
          return response;
        }
        // No payload means no session was established. Reject rather than resolving
        // falsy, so the caller cannot mistake this for a successful login.
        throw new Error("Login response contained no data");
      });
    // Deliberately no .catch() here. Swallowing the rejection let Arcid.vue's .then()
    // run after a failed login and redirect an unauthenticated user into the app,
    // where AuthenticatedLayout bounced them straight back to /login with no
    // explanation. Arcid.vue's .catch() renders the real error instead.
  },

  /**
   * Obtains a new access token using the stored refresh token.
   * Returns the response data on success.
   * Throws TransientRefreshError for network errors / 5xx (caller should retry).
   * Calls logout() and returns false for 401/403 (refresh token genuinely rejected).
   *
   * Every tab reads the session from localStorage once, when it loads, and ARC ID refresh
   * tokens can be used once. So another tab may already have used the token this one holds:
   * its session, saved since, is taken over -- before refreshing, and after a refusal -- rather
   * than sending a spent token or ending the session.
   */
  async getNewAccessToken() {
    const saved = this.sessionSavedByAnotherTab();
    if (saved) {
      this.adoptSession(saved);
      if (store.authentication.expires_at > this.currentTimeInSeconds() + EXPIRY_BUFFER_SECONDS) {
        return { access_token: store.authentication.accessToken };
      }
    }

    if (!store.authentication.refreshToken) return false;

    // What this request redeems: a session taken over from another tab can replace it while
    // the request is open.
    const submittedRefreshToken = store.authentication.refreshToken;

    let response;
    try {
      response = await arcReadsApiClient.post("/refresh", {
        refresh_token: submittedRefreshToken,
        client_id: import.meta.env.VITE_ARCID_CLIENT_ID,
      });
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        // Refused because another tab used the same token first: the session is alive in that
        // tab's save, not over.
        if (await this.adoptRotatedSession(submittedRefreshToken)) {
          console.warn('Refresh token used by another tab; carrying on with its session.');
          if (store.authentication.expires_at > this.currentTimeInSeconds() + EXPIRY_BUFFER_SECONDS) {
            return { access_token: store.authentication.accessToken };
          }
          return this.getNewAccessToken();
        }
        console.error('Refresh token rejected:', status);
        this.endRejectedSession(submittedRefreshToken);
        return false;
      }
      // Network error, 5xx, or anything else — transient, may recover on retry.
      const message = err?.message ? String(err.message) : `Refresh failed with status ${status ?? 'unknown'}`;
      throw new TransientRefreshError(message);
    }

    if (response && response.data) {
      this.processLoginResponse(response);
      return response.data;
    }

    throw new TransientRefreshError('Refresh returned empty response');
  },

  /**
   * Who a session belongs to, as the API's `me` names them: a staff user or a student. Null
   * when it can't be told, and then no other tab's session is ever taken over.
   */
  accountOf(user): string | null {
    if (user?.userId > 0) return `user.${user.userId}`;
    if (user?.studentId != null) return `student.${user.studentId}`;
    return null;
  },

  /**
   * A session another tab has saved with a refresh token this tab doesn't hold, for the same
   * account. Its refresh token is the live one: ARC ID spent this tab's when that tab used it.
   *
   * Only ever the same account. On a shared device a second student can sign in while the
   * first one's tab is still open, and carrying on as them would be far worse than signing in
   * again.
   */
  sessionSavedByAnotherTab(): any {
    const saved = readSavedStore()?.authentication;
    if (!saved?.refreshToken || !saved.accessToken || saved.expires_at == null) return null;
    if (saved.refreshToken === store.authentication.refreshToken) return null;

    const account = this.accountOf(store.authentication.user);
    if (!account || this.accountOf(saved.user) !== account) return null;

    return saved;
  },

  /**
   * Carries on with another tab's tokens. The rest of this tab's session -- its user, profile
   * and steps -- is the same account's, and stays as this tab has it.
   */
  takeOverSession(saved) {
    store.authentication.accessToken = saved.accessToken;
    store.authentication.refreshToken = saved.refreshToken;
    store.authentication.expires_at = saved.expires_at;
  },

  adoptSession(saved) {
    this.takeOverSession(saved);
    saveStore();
  },

  /**
   * After a refused refresh, takes over the session another tab saved by redeeming the same
   * token first. Its save can land just after the refusal, so it looks again a couple of times.
   * Resolves to whether there was one to take over.
   */
  async adoptRotatedSession(submittedRefreshToken: string): Promise<boolean> {
    // Without an account to match, no other tab's session could be taken over: don't wait for one.
    if (!this.accountOf(store.authentication.user)) return false;

    for (let attempt = 0; ; attempt++) {
      // Already taken over while the request was open.
      if (store.authentication.refreshToken !== submittedRefreshToken) return true;

      const saved = this.sessionSavedByAnotherTab();
      if (saved) {
        this.adoptSession(saved);
        return true;
      }
      if (attempt >= ADOPT_RETRY_ATTEMPTS) return false;
      await new Promise(resolve => setTimeout(resolve, ADOPT_RETRY_DELAY_MS));
    }
  },

  /**
   * Ends this tab's session after its refresh token was refused. The copy in localStorage is
   * shared, so it is only cleared -- and ARC ID signed out of -- if it is this tab's own.
   *
   * A refresh token there that this tab didn't send belongs to another tab: another account
   * signed in on this device, or this one saved in a way this tab couldn't take over. Clearing
   * it, or signing out of ARC ID, would end that tab's session too. This tab goes to the login
   * page instead, without writing anything, and the page picks up whatever session is saved.
   */
  endRejectedSession(submittedRefreshToken: string) {
    const saved = readSavedStore()?.authentication;
    if (saved?.refreshToken && saved.refreshToken !== submittedRefreshToken) {
      stopWriting();
      store.authentication = {};
      window.location.href = "/login";
      return;
    }
    this.logout();
  },

  async getNewAccessTokenWithRetry() {
    for (let attempt = 1; attempt <= RETRY_MAX_ATTEMPTS; attempt++) {
      try {
        return await this.getNewAccessToken();
      } catch (err) {
        if (!(err instanceof TransientRefreshError)) throw err;
        if (attempt === RETRY_MAX_ATTEMPTS) {
          console.error(`Token refresh failed after ${RETRY_MAX_ATTEMPTS} attempts`);
          throw err;
        }
        const delay = RETRY_BASE_DELAY_MS * Math.pow(2, attempt - 1);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  },

  addFeature(featureToAdd) {
    const featureIndex = store.authentication.features?.findIndex((feature) => feature === featureToAdd);
    if (featureIndex === -1) {
      store.authentication.features.push(featureToAdd);
    }
  },

  checkForFeatures() {
    if (checkingForFeatures.value) return;
    store.authentication.featuresLoadError = false;
    checkingForFeatures.value = true;
    rosterApiClient.get(`/student/licenses`).then(response => {
      const licenses = response.data?.schoolLicenses;
      if (!store.authentication.features) store.authentication.features = [];
      this.processLicenses(licenses);
      store.authentication.featuresLoaded = true;
    })
    .catch((error) => {
      console.error('Error fetching licenses:', error);
      store.authentication.featuresLoadError = true;
      if (!store.authentication.features) store.authentication.features = [];
      this.processLicenses([]);
    })
    .finally(() => {
      checkingForFeatures.value = false;
    });
  },

  removeFeature(featureToRemove) {
    const featureIndex = store.authentication.features?.findIndex((feature) => feature === featureToRemove);
    if (featureIndex != null && featureIndex !== -1) {
      store.authentication.features.splice(featureIndex, 1);
    }
  },

  processLicenses(licenses) {
    if (!Array.isArray(licenses)) return;
    const featureMap = {
      'arcreads-assignments': 'hasAssignments',
      'arcreads-logbook': 'hasLogbook',
      'arcreads-myirla': 'hasMyIrla'
    };
    Object.entries(featureMap).forEach(([featureName, key]) => {
      licenses.some((license) => license[key])
        ? this.addFeature(featureName)
        : this.removeFeature(featureName);
    });
  },

  processLoginResponse(response, isInitialLogin = false) {
    return new Promise((resolve) => {
      if (response.data.hasArcReads == false) {
        window.location.href = "/access-check";
        return;
      }

      const expiresInSeconds = response.data.expires_in ?? 900;

      logService.clearCombinedReadingLog();
      store.authentication.accessToken = response.data.access_token;
      store.authentication.refreshToken = response.data.refresh_token;

      store.authentication.expires_at = this.currentTimeInSeconds() + expiresInSeconds;
      store.authentication.user = response.data.me;
      if (response.data.me.districtId) unleash.setContextField("districtId", response.data.me.districtId);
      if (response.data.me.currentInstance?.gradeId) unleash.setContextField("gradeId", response.data.me.currentInstance.gradeId);
      store.authentication.authenticating = false;
      store.authentication.user.profile = response.data.profile;
      store.authentication.user.readingSteps = response.data.readingSteps;
      store.authentication.user.pastSchoolYears = response.data.pastSchoolYears;
      store.authentication.user.bookshelfSchoolIds = response.data.bookshelfSchoolIds ?? [];
      store.authentication.user.assessments = response.data.assessments;
      // The current school year, dated by the student's own school, comes with every login and token
      // refresh, so it can't go stale the way a once-per-sign-in fetch did.
      store.appData.thisSchoolYear = response.data.schoolYear ?? null;

      // Make the dates consistent with arc reads date formats
      store.authentication.user.readingSteps.forEach((entry) => {
        entry.entry_date = entry.entry_date + " 00:00:00";
      });

      // To support automated tests, serialize the updates immediately (do not debounce)
      logService.clearCombinedReadingLog();
      this.updateStore();

      // Only fire on a fresh login, not on silent token refreshes — listeners
      // (e.g. App.vue loading genres/recommendations) should not run mid-session,
      // and we don't need to refetch licenses every 15 minutes.
      if (isInitialLogin) {
        if (response.data.me.currentInstance) {
          this.checkForFeatures()
        }
        eventBus.$emit("loggedIn");
      }
      resolve(response.data);
    });
  },

  isFeatureEnabled(featureNameToCheck) {
    let enabled = false;
    if (store.authentication.features) {
      const foundFeature = store.authentication.features.find(
        (feature) => feature === featureNameToCheck
      );
      return foundFeature;
    }
    return enabled;
  },

  clearStore() {
    store.loaded = false;
    store.appData = {};
    store.authentication = {};
    // Stale cooldowns/timestamps from the prior session would otherwise gate
    // validateSession for the next login (e.g. a failure cooldown rejecting
    // the first post-login API call).
    lastValidationTimestamp = 0;
    lastRefreshFailureTimestamp = 0;
    refreshPromise = null;
    this.updateStore();
  },

  updateStore() {
    saveStore();
  },
};

// The store writes itself half a second after any change, tokens included. Before it does, take
// over a session another tab has saved since this one last looked, so this tab's spent refresh
// token is never written over the live one. Every other change this tab made is still written.
onBeforeDeferredWrite(() => {
  const saved = authenticationService.sessionSavedByAnotherTab();
  if (saved && saved.refreshToken !== lastSeenRefreshToken()) {
    authenticationService.takeOverSession(saved);
  }
});

export default authenticationService;
